import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, pillClass, statusLabel, timeAgo } from "../lib/api";
import { Plus, Filter, Download } from "lucide-react";

const STATUSES = ["all", "new", "acknowledged", "assigned", "in_progress", "resolved", "closed"];
const CATEGORIES = ["all", "plumbing", "electrical", "security", "housekeeping", "parking", "water", "lift", "noise", "vendor", "admin", "suggestion", "other"];

export default function IssuesListPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "all";
  const category = params.get("category") || "all";
  const [items, setItems] = useState([]);

  useEffect(() => {
    const q = {};
    if (status !== "all") q.status = status;
    if (category !== "all") q.category = category;
    api.get("/issues", { params: q }).then((r) => setItems(r.data));
  }, [status, category]);

  function update(k, v) {
    const np = new URLSearchParams(params);
    if (v === "all") np.delete(k); else np.set(k, v);
    setParams(np);
  }

  function exportCsv() {
    const u = `${process.env.REACT_APP_BACKEND_URL}/api/export/issues`;
    window.open(u, "_blank");
  }

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-5xl">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Issues</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">All issues</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} data-testid="issues-export"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-stone-300 text-sm hover:border-stone-500"><Download size={14} /> Export</button>
          <Link to="/issues/new" data-testid="new-issue-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C05640] hover:bg-[#A54734] text-white font-medium">
            <Plus size={18} /> Raise an issue
          </Link>
        </div>
      </header>

      <div className="mt-5 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="status-filters">
        <Filter size={14} className="text-stone-400 shrink-0" />
        {STATUSES.map((s) => (
          <button key={s} onClick={() => update("status", s)} data-testid={`filter-chip-${s}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              status === s ? "bg-[#C05640] text-white border-[#C05640]" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
            }`}>{s === "all" ? "All" : statusLabel(s)}</button>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
        <span className="text-xs text-stone-500 shrink-0">Category:</span>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => update("category", c)} data-testid={`filter-cat-${c}`}
            className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] capitalize border ${
              category === c ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200"
            }`}>{c}</button>
        ))}
      </div>

      <div className="mt-5 space-y-2.5">
        {items.length === 0 && <div className="text-sm text-stone-500 italic py-10 text-center">No issues match these filters.</div>}
        {items.map((i) => {
          const thumb = (i.attachments || []).find((a) => (a.content_type || "").startsWith("image/"));
          const thumbUrl = thumb ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${thumb.storage_path}` : null;
          return (
            <Link key={i.id} to={`/issues/${i.id}`} data-testid={`issue-card-${i.id}`}
                  className="block bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-400 transition">
              <div className="flex items-start gap-3">
                {thumbUrl && (
                  <img src={thumbUrl} alt="" crossOrigin="use-credentials"
                       data-testid={`issue-thumb-${i.id}`}
                       className="w-16 h-16 rounded-xl object-cover border border-stone-200 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-stone-500">#{i.number}</span>
                    <span className="font-medium text-stone-900">{i.title}</span>
                  </div>
                  <div className="text-xs text-stone-500 mt-1">
                    <span className="capitalize">{i.category}</span> · {i.is_common_area ? "Common area" : "Flat issue"} · raised by {i.creator_name} · {timeAgo(i.created_at)}
                  </div>
                  {i.assignee_name && <div className="text-xs text-stone-600 mt-0.5">Assigned to <strong>{i.assignee_name}</strong></div>}
                  {i.ai_summary && <div className="text-xs text-stone-600 mt-1.5 italic">{i.ai_summary}</div>}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span className={pillClass(i.status)}>{statusLabel(i.status)}</span>
                  <span className={pillClass(i.priority)}>{i.priority}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
